import { ComboBox, Inputs } from '@cotera/client/app/components/forms';
import {
  Button,
  Icon,
  IconName,
  Tooltip,
} from '@cotera/client/app/components/ui';
import { Assert } from '@cotera/utilities';
import {
  FormStateReturn,
  makeUseFormField,
  Path,
  useFormState,
} from '@cotera/client/app/components/forms/hooks/use-form';
import { useEffect, useState } from 'react';
import { ChildrenProps, classNames } from '@cotera/client/app/components/utils';
import { Text } from '@cotera/client/app/components/ui';
import { ConnectDragSource, DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 } from 'uuid';
import { And, Expression, Relation, TC, Ty } from '@cotera/era';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
} from '@cotera/client/app/components/headless';

type ColumnNode = {
  t: 'column-ref';
  columnName: string;
  filter?: Expression;
};

type EnumItemNode = {
  t: 'enum-item';
};

type InlineNode = {
  t: 'inline';
  filter?: Expression;
};

export type TaxonomyNode = {
  id: string;
  name: string;
  children: {
    [key: string]: TaxonomyNode;
  };
} & (ColumnNode | InlineNode | EnumItemNode);

export type FormState = FormStateReturn<TaxonomyNode>;

export const createPath = (
  path: Path,
  formState: Pick<FormState, 'getField'>
): Path => {
  if (path.length === 0) {
    return path;
  }

  const parentPath = createPath(path.slice(0, -1), formState);
  const parent = formState.getField(parentPath);

  if (parent === undefined) {
    return path;
  }

  return [...parentPath, 'children', path.at(-1)!].filter(
    (x) => x !== undefined
  );
};

const useFormField = makeUseFormField<TaxonomyNode>((path, formState) => {
  return createPath(path, formState);
});

export const TaxonomyTypeBuilder: React.FC<{
  value: TaxonomyNode;
  onChange: (value: TaxonomyNode) => void;
  onEditClick: (path: Path, formState: FormState) => void;
  rel: Relation;
  actions: {
    label: string;
    icon: IconName;
    disabled?: boolean;
    onClick: (path: Path, formState: FormState) => void;
  }[];
  columnName: string;
  context?: (props: { path: Path; formState: FormState }) => React.ReactNode;
}> = ({
  onChange,
  value: initialType,
  onEditClick,
  rel,
  actions,
  context,
  columnName,
}) => {
  const formState = useFormState<TaxonomyNode>({
    id: v4(),
    name: 'root',
    t: 'inline',
    children: {
      [initialType.name]: initialType,
    },
  });

  useEffect(() => {
    const unsubscribe = formState.subscribe(() => {
      const root = formState.getField(createPath(['root'], formState))!;
      onChange(root);
    });

    return () => {
      unsubscribe();
    };
  }, [formState, onChange]);

  return (
    <div className="max-h-[70vh] overflow-y-auto">
      <DndProvider backend={HTML5Backend}>
        <Renderer
          columnName={columnName}
          context={context}
          actions={actions}
          canManipulateInPlace
          node={initialType}
          formState={formState}
          path={[]}
          onEditClick={onEditClick}
          rel={rel}
        />
      </DndProvider>
    </div>
  );
};

type RendererProps = {
  node: TaxonomyNode;
  formState: FormState;
  path: Path;
  onEditClick: (path: Path, formState: FormState) => void;
  canManipulateInPlace: boolean;
  columnName: string;
  rel: Relation;
  actions: {
    label: string;
    icon: IconName;
    onClick: (path: Path, formState: FormState) => void;
    disabled?: boolean;
  }[];
  context?: (props: { path: Path; formState: FormState }) => React.ReactNode;
};

const Group: React.FC<ChildrenProps & { depth: number }> = ({ children }) => {
  return <div className={classNames('flex flex-col w-full')}>{children}</div>;
};

const ObjectItemRenderer: React.FC<
  Omit<RendererProps, 'onEditClick'> & {
    dragRef: ConnectDragSource;
  }
> = (props) => {
  const [value, setValue] = useFormField(props.formState, props.path);
  const [parentValue] = useFormField(props.formState, props.path.slice(0, -1));
  const item = value as TaxonomyNode & { name: string };

  // should check parent.canManipulateInPlace
  const disabled =
    props.path.length === 1 ||
    (parentValue?.t !== 'inline' && value?.t === 'enum-item');

  if (props.path.length === 1) {
    return null;
  }

  return (
    <>
      <Inputs.Text
        customIcon={
          props.path.length > 1 &&
          props.canManipulateInPlace && (
            <Button
              ref={props.dragRef}
              icon="drag-handle"
              iconOnly
              className="pl-0! pr-0! cursor-move!"
              inline
              small
              disabled={disabled}
            />
          )
        }
        disabled={disabled}
        value={item.name}
        onChange={(v) => {
          if (!disabled) {
            setValue((prev) => {
              return {
                ...prev,
                name: v,
              };
            });
          }
        }}
      />
      {value?.t === 'column-ref' && !isBooleanColumn(value, props) && (
        <ComboBox.Single
          className="text-semibold"
          value={{
            display: value.columnName,
            value: value.columnName,
          }}
          options={({ query }) => (
            <ComboBox.StaticOptions
              query={query}
              dynamicEnabled={false}
              options={Object.entries(props.rel.attributes)
                .filter(([_, v]) => Ty.isEnum(v) || TC.isBoolean(v))
                .filter(([k]) => k !== props.columnName)
                .map(([k, v]) => ({
                  value: k,
                  display: k,
                  context: Ty.displayTy(v),
                }))}
            />
          )}
          onChange={(v) => {
            if (v) {
              const ty = props.rel.attributes[v.value]!;

              if (Ty.isEnum(ty)) {
                const enumTy = ty.ty;
                Assert.assert(enumTy.k === 'enum', 'enum type expected');
                setValue((prev) => ({
                  ...prev,
                  columnName: v?.value ?? '',
                  children: Object.fromEntries(
                    enumTy.t.map((v) => [
                      v,
                      {
                        id: v4(),
                        t: 'enum-item',
                        name: v,
                        children: {},
                      },
                    ])
                  ),
                }));
              } else if (TC.isBoolean(ty)) {
                setValue((prev) => ({
                  ...prev,
                  t: 'inline',
                  filter: props.rel.ref('from').attr(v.value).eq(true),
                  children: {},
                }));
              } else {
                setValue((prev) => ({
                  ...prev,
                  columnName: v.value ?? '',
                  children: {},
                }));
              }
            }
          }}
        />
      )}
    </>
  );
};

const Renderer: React.FC<RendererProps> = (props) => {
  const [value] = useFormField(props.formState, props.path);
  Assert.assert(
    value !== undefined,
    `no value at path ${props.path.join('.')}`
  );

  return (
    <Group depth={props.path.length}>
      {Object.entries(value.children).map(([key, v], index) => {
        return (
          <ItemRenderer
            columnName={props.columnName}
            context={props.context}
            actions={props.actions}
            canManipulateInPlace={
              props.canManipulateInPlace === false
                ? false
                : value.t === 'inline'
            }
            index={index}
            rel={props.rel}
            onEditClick={props.onEditClick}
            key={
              (props.path.length > 0 ? `${props.path.join('.')} ${key}` : key) +
              v.id
            }
            formState={props.formState}
            path={[...props.path, key]}
          />
        );
      })}
    </Group>
  );
};

const DropArea: React.FC<{
  path: Path;
  formState: FormState;
  index: number;
}> = ({ path, formState, index }) => {
  const [value] = useFormField(formState, path);
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'item',
      drop: (item: { path: Path; id: string; index: number }) => {
        const fromField = formState.getField(
          createPath(item.path.slice(0, -1), formState)
        )!;

        const parentPath = item.path.slice(0, -1);

        const targetPath = path.slice(0, -1);
        const subjectField = formState.getField(
          createPath(item.path, formState)
        );
        const targetField = formState.getField(
          createPath(targetPath, formState)
        )!;

        if (item.path.slice(0, -1).join('.') !== targetPath.join('.')) {
          //then remove
          formState.updateField(createPath(parentPath, formState), {
            ...fromField,
            children: {
              ...Object.fromEntries(
                Object.entries(fromField.children).filter(
                  ([_, x]) => x.id !== item.id
                )
              ),
            },
          });

          const childrenArr = Object.entries(targetField.children);

          const newChildren = [
            ...childrenArr.slice(0, index),
            [item.path.at(-1)!, subjectField],
            ...childrenArr.slice(index),
          ];
          //and add to target
          formState.updateField(createPath(targetPath, formState), {
            ...targetField,
            children: Object.fromEntries(newChildren),
          });
        } else {
          //then move
          const childrenArr = Object.entries(fromField.children).filter(
            ([_], index) => index !== item.index
          );

          const newChildren = [
            ...childrenArr.slice(0, index),
            [subjectField!.name, subjectField],
            ...childrenArr.slice(index),
          ];

          formState.updateField(createPath(parentPath, formState), {
            ...fromField,
            children: Object.fromEntries(newChildren),
          });
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [...path, index, value]
  );

  return (
    <div
      className={classNames(
        'w-full max-h-5 flex items-center justify-center transition-all ease-in-out duration-200 overflow-hidden shrink-0',
        isOver
          ? 'h-12 opacity-100 border-dashed border-divider border bg-indigo-50 mb-2'
          : 'h-2 opacity-0'
      )}
      ref={drop}
    >
      {value && <Text.Caption>Place above {value.name}</Text.Caption>}
    </div>
  );
};

const isBooleanColumn = (
  value: TaxonomyNode,
  props: Pick<RendererProps, 'rel'>
) =>
  value.t === 'column-ref' &&
  props.rel.attributes[value.columnName]?.ty &&
  TC.isBoolean(props.rel.attributes[value.columnName]!.ty);

const ItemRenderer: React.FC<
  Omit<RendererProps, 'node'> & { index: number }
> = (props) => {
  const [_, setParentValue] = useFormField(
    props.formState,
    props.path.slice(0, -1)
  );

  const [value, setValue] = useFormField(props.formState, props.path);
  const [collapsed, setCollapsed] = useState(false);

  Assert.assert(
    value !== undefined,
    `no value at path ${props.path.join('.')}`
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'item',
      item: {
        path: props.path,
        id: value?.id,
        index: props.index,
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [props.path, props.index, value]
  );

  const canManipulateInPlace =
    props.canManipulateInPlace === false ? false : value.t !== 'enum-item';
  const hasChildren = Object.keys(value.children).length > 0;

  const getFilter = () => {
    if (value?.t === 'inline') {
      return value?.filter?.toEraQL();
    }

    if (value?.t === 'column-ref') {
      if (isBooleanColumn(value, props)) {
        if (value.filter) {
          return And(
            props.rel.ref('from').attr(value.columnName),
            value.filter
          ).toEraQL();
        }

        return props.rel.ref('from').attr(value.columnName).eq(true).toEraQL();
      }

      if (value?.filter) {
        return value?.filter?.toEraQL();
      }
    }

    return null;
  };

  const getIncompleteWarning = () => {
    if (value.t === 'column-ref' && value.columnName.length === 0) {
      return 'Please reference a column';
    }

    if (value.t === 'inline' && value.name.length === 0) {
      return 'Please add a name';
    }

    return null;
  };

  const isCatchAll = () => {
    if (value.t === 'inline') {
      return (
        Object.keys(value.children).length === 0 &&
        (!value.filter || filter === 'true')
      );
    }

    return false;
  };

  const filter = getFilter();

  return (
    <div className="flex flex-col w-full">
      {props.path.length > 1 && (
        <DropArea
          path={props.path}
          formState={props.formState}
          index={props.index}
        />
      )}
      {!canManipulateInPlace && <div className="h-2" />}
      <div className="flex items-center">
        {value.t === 'column-ref' && (
          <Text.Caption className={classNames('mb-2 text-nowrap', 'ml-7')}>
            Column Reference
          </Text.Caption>
        )}
        {value.t === 'column-ref' && filter !== null && (
          <Text.Caption className="mb-2 ml-1 mr-1"> | </Text.Caption>
        )}
        {filter !== null && filter !== 'true' && (
          <Text.Caption
            className={classNames(
              'mb-2 text-nowrap text-ellipsis overflow-hidden',
              value.t === 'inline' ? 'ml-7' : 'ml-2'
            )}
          >
            {filter}
          </Text.Caption>
        )}
      </div>
      <div
        className={classNames(
          'flex items-center w-full space-x-2',
          isDragging ? 'opacity-50' : ''
        )}
      >
        {hasChildren && props.path.length > 1 && (
          <Button
            icon={collapsed ? 'chevron-right' : 'chevron-down'}
            iconOnly
            onClick={() => setCollapsed((prev) => !prev)}
            inline
            className="pl-0! -ml-1.5"
          />
        )}
        {!hasChildren && <Text.Caption className="w-4 h-4">-</Text.Caption>}
        <ObjectItemRenderer
          columnName={props.columnName}
          actions={props.actions}
          rel={props.rel}
          canManipulateInPlace={canManipulateInPlace}
          dragRef={drag}
          formState={props.formState}
          path={props.path}
          node={value}
        />
        {canManipulateInPlace && props.path.length > 1 && (
          <Dropdown>
            <DropdownTrigger>
              <Button icon="ellipsis-vertical" className="px-0!" inline />
            </DropdownTrigger>
            <DropdownContent priority="z-50">
              {(value.t === 'inline' || isBooleanColumn(value, props)) && (
                <DropdownItem
                  closeOnClick
                  icon={<Icon icon="add" />}
                  onClick={() => {
                    if (isBooleanColumn(value, props)) {
                      Assert.assert(
                        value.t === 'column-ref',
                        'boolean column ref expected'
                      );

                      setValue((prev) => {
                        return {
                          ...prev,
                          t: 'inline',
                          filter: And(
                            props.rel.ref('from').attr(value.columnName),
                            value.filter ?? true
                          ),
                          children: {
                            ...prev.children,
                            [v4()]: {
                              id: v4(),
                              t: 'inline',
                              name: '',
                              children: {},
                            },
                          },
                        };
                      });
                    } else {
                      setValue((prev) => {
                        return {
                          ...prev,
                          children: {
                            ...prev.children,
                            [v4()]: {
                              id: v4(),
                              t: 'inline',
                              name: '',
                              children: {},
                            },
                          },
                        };
                      });
                    }
                  }}
                >
                  Add Sub Category
                </DropdownItem>
              )}
              {props.path.length > 1 &&
                value.t !== 'column-ref' &&
                Object.keys(value.children).length === 0 && (
                  <DropdownItem
                    closeOnClick
                    icon={<Icon icon="code-bracket" />}
                    onClick={() => {
                      setParentValue((prev) => {
                        return {
                          ...prev,
                          children: {
                            ...prev.children,
                            [String(props.path.at(-1)!)]: {
                              id: v4(),
                              t: 'column-ref',
                              columnName: '',
                              name: value.name,
                              children: {},
                            },
                          },
                        };
                      });
                    }}
                  >
                    Reference a column
                  </DropdownItem>
                )}
              {props.actions.map((a) => (
                <DropdownItem
                  closeOnClick
                  icon={<Icon icon={a.icon} />}
                  disabled={a.disabled}
                  onClick={() => a.onClick(props.path, props.formState)}
                >
                  {a.label}
                </DropdownItem>
              ))}
              {value.t !== 'enum-item' && props.path.length > 1 && (
                <DropdownItem
                  closeOnClick
                  icon={<Icon icon="edit" />}
                  onClick={() => {
                    props.onEditClick(props.path, props.formState);
                  }}
                >
                  {value.t === 'inline' ? 'Edit Category' : 'Edit Filter'}
                </DropdownItem>
              )}
              {props.path.length > 1 && (
                <DropdownItem
                  closeOnClick
                  icon={<Icon icon="trash" className="text-destructive" />}
                  onClick={() => {
                    setParentValue((prev) => {
                      const newFields = { ...prev.children };
                      delete newFields[props.path.at(-1)!];
                      return {
                        ...prev,
                        children: newFields,
                      };
                    });
                  }}
                >
                  Delete
                </DropdownItem>
              )}
            </DropdownContent>
          </Dropdown>
        )}
        {props.context &&
          props.context({
            path: props.path,
            formState: props.formState,
          })}
        {getIncompleteWarning() && props.path.length > 1 && (
          <Tooltip tooltipContent={getIncompleteWarning()} side="top">
            <div className="flex items-center">
              <Icon icon="exclamation-triangle" className="text-destructive" />
            </div>
          </Tooltip>
        )}
        {isCatchAll() && props.path.length > 1 && (
          <Tooltip
            tooltipContent="This category is a catch all, it will match all values"
            side="top"
          >
            <div className="flex items-center">
              <Icon icon="exclamation-circle" className="text-destructive" />
            </div>
          </Tooltip>
        )}
      </div>
      <div
        className={classNames(
          'flex flex-col w-full mb-2',
          props.path.length > 1 ? 'border-l pl-10 border-divider' : '',
          collapsed ? 'hidden' : ''
        )}
      >
        <Renderer
          columnName={props.columnName}
          actions={props.actions}
          rel={props.rel}
          canManipulateInPlace={
            props.canManipulateInPlace === false ? false : value.t === 'inline'
          }
          formState={props.formState}
          path={props.path}
          node={value}
          onEditClick={props.onEditClick}
          context={props.context}
        />
      </div>
      {props.path.length === 1 && value.t === 'inline' && (
        <Button
          icon="add"
          className="w-fit"
          tooltip="right"
          theme="primary"
          text="Add Category"
          onClick={() => {
            setValue((prev) => {
              return {
                ...prev,
                children: {
                  ...prev.children,
                  [v4()]: {
                    id: v4(),
                    t: 'inline',
                    name: '',
                    children: {},
                  },
                },
              };
            });
          }}
        />
      )}
    </div>
  );
};
